/**
 * Lógica de "Meus Desktops" — dados + ações, sem UI.
 *
 * Fonte única para as DUAS superfícies:
 *   - web     → MyDesktopsBar (barra no topo)
 *   - desktop → menu nativo "Favoritos" (NativeFavoritesMenu)
 *
 * O menu nativo é uma VIEW disto, não uma segunda implementação: quem clica no
 * menu chama exatamente o mesmo `switchTo()` que a barra chama.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { confirmDialog } from "primereact/confirmdialog";
import { useChartsStore } from "~/store/chartStore";
import useWorkspaceStore, { getWorkspaceContext } from "~/store/workspaceStore";
import { useSettingsStore } from "~/store/settingsStore";
import { eventBus, EVENTS } from "~/utils/eventBus";
import { listDesktopsRemote } from "~/workspace";
import {
  switchToDesktop,
  reloadDesktopFromServer,
  isDesktopRestoreSettling,
} from "~/workspace/desktopManager";
import {
  listDesktopCacheStates,
  isCurrentStateDirty,
} from "~/workspace/storage/desktopCache";

/** Live dirty re-check cadence for the active desktop (off the render path) */
const DIRTY_POLL_MS = 10000;

/**
 * @param {boolean} enabled - só busca/pollia quando a superfície está visível
 */
export const useMyDesktops = (enabled) => {
  const favoriteDesktops = useSettingsStore(
    (state) => state.favoriteDesktops || [],
  );
  const toggleFavoriteDesktop = useSettingsStore(
    (state) => state.toggleFavoriteDesktop,
  );
  const currentDesktopId = useWorkspaceStore((state) => state.currentDesktopId);
  const desktopName = useWorkspaceStore((state) => state.desktopName);

  const [desktops, setDesktops] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [cacheStates, setCacheStates] = useState({});
  const [activeDirty, setActiveDirty] = useState(false);
  const switchingRef = useRef(false);

  const refreshDirtyStates = useCallback(() => {
    const { user, broker } = getWorkspaceContext();
    if (!user || !broker) return;
    setCacheStates(listDesktopCacheStates(user, broker));
    // Read the id from the STORE, not the render closure — right after a
    // switch the closure still holds the PREVIOUS desktop, and comparing the
    // new state against the old baseline flashes a spurious *.
    const activeId = useWorkspaceStore.getState().currentDesktopId;
    if (activeId != null) {
      // Mid-restore the live state is partial and reads as a spurious dirty —
      // skip until the restore settles (the next poll shows the real state)
      setActiveDirty(
        !isDesktopRestoreSettling(activeId) &&
          isCurrentStateDirty(
            useChartsStore.getState(),
            user,
            broker,
            activeId,
          ),
      );
    } else {
      setActiveDirty(false);
    }
  }, []);

  const fetchDesktops = useCallback(async () => {
    try {
      const list = await listDesktopsRemote();
      setDesktops(list || []);
      setLoaded(true);
    } catch (error) {
      // Keep the previous list — this is not worth an error toast
      console.warn("[MyDesktops] Failed to fetch desktop list:", error);
    }
  }, []);

  // Fetch on mount / after load-save flows (desktop id or name changed) and
  // when the list is mutated elsewhere (save/rename/delete/duplicate).
  // Saving captures a fresh baseline, so the dirty * must clear right away —
  // refresh dirty states together with the list.
  useEffect(() => {
    if (!enabled) return undefined;
    const onListChanged = () => {
      fetchDesktops();
      refreshDirtyStates();
    };
    onListChanged();
    eventBus.on(EVENTS.DESKTOP_LIST_CHANGED, onListChanged);
    return () => eventBus.off(EVENTS.DESKTOP_LIST_CHANGED, onListChanged);
  }, [
    enabled,
    fetchDesktops,
    refreshDirtyStates,
    currentDesktopId,
    desktopName,
  ]);

  // Poll the active desktop's live dirty state (cheap: hash of the snapshot)
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(refreshDirtyStates, DIRTY_POLL_MS);
    return () => clearInterval(id);
  }, [enabled, refreshDirtyStates]);

  // Migration path: adopt the id when only the name of the active desktop is
  // known (desktops loaded before "Meus Desktops" tracked ids)
  useEffect(() => {
    if (!enabled || currentDesktopId != null) return;
    if (!desktopName || desktopName === "Novo desktop") return;
    const match = desktops.find((d) => d.name === desktopName);
    if (match) {
      useWorkspaceStore.getState().setCurrentDesktopId(match.id);
    }
  }, [enabled, currentDesktopId, desktopName, desktops]);

  // Content rule: with ≥1 favorite show only favorites, otherwise the first 5.
  const sections = useMemo(() => {
    const favorites = desktops.filter((d) => favoriteDesktops.includes(d.id));
    return favorites.length > 0 ? favorites : desktops.slice(0, 5);
  }, [desktops, favoriteDesktops]);

  // Does the user have at least one favorite among the available desktops? Lets a
  // surface distinguish "showing favorites" from the first-5 fallback (the web bar
  // renders an empty-state instead of the fallback). Gate any "no favorites" UI on
  // `loaded` too, or it flashes before the first fetch resolves.
  const hasFavorites = useMemo(
    () => desktops.some((d) => favoriteDesktops.includes(d.id)),
    [desktops, favoriteDesktops],
  );

  // Desktop atualmente carregado (para o "você está aqui" quando ele não está
  // entre os favoritos da barra). Objeto real da lista; fallback pelo nome do
  // workspace enquanto a lista não tem o id (import recém-criado).
  const currentDesktop = useMemo(() => {
    if (currentDesktopId == null) return null;
    return (
      desktops.find((d) => d.id === currentDesktopId) ?? {
        id: currentDesktopId,
        name: desktopName || "Desktop atual",
      }
    );
  }, [desktops, currentDesktopId, desktopName]);

  const isDirty = useCallback(
    (desktop) =>
      desktop.id === currentDesktopId
        ? activeDirty
        : (cacheStates[String(desktop.id)]?.dirty ?? false),
    [currentDesktopId, activeDirty, cacheStates],
  );

  const switchTo = useCallback(
    async (desktop) => {
      if (switchingRef.current || desktop.id === currentDesktopId) return;
      switchingRef.current = true;
      try {
        const chartsStore = useChartsStore.getState();
        const wrappedLoadAction = (...args) => {
          chartsStore.clearWorkspace();
          return useChartsStore.getState().loadWorkspace(...args);
        };
        await switchToDesktop(desktop.id, desktop.name, {
          getChartsState: () => useChartsStore.getState(),
          loadCharts: wrappedLoadAction,
        });
      } catch (error) {
        console.error("[MyDesktops] Failed to switch desktop:", error);
      } finally {
        switchingRef.current = false;
        refreshDirtyStates();
        // Re-check once the restore settles (live dirty is suppressed during
        // the settle window; a dirty local copy must show its * promptly)
        setTimeout(refreshDirtyStates, 3500);
      }
    },
    [currentDesktopId, refreshDirtyStates],
  );

  const confirmReload = useCallback(
    (desktop) => {
      confirmDialog({
        message: `Descartar mudanças locais de "${desktop.name}"?`,
        header: "Recarregar do servidor",
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        acceptLabel: "Descartar e recarregar",
        rejectLabel: "Cancelar",
        accept: async () => {
          try {
            const chartsStore = useChartsStore.getState();
            const wrappedLoadAction = (...args) => {
              chartsStore.clearWorkspace();
              return useChartsStore.getState().loadWorkspace(...args);
            };
            await reloadDesktopFromServer(desktop.id, desktop.name, {
              getChartsState: () => useChartsStore.getState(),
              loadCharts: wrappedLoadAction,
            });
          } catch (error) {
            console.error("[MyDesktops] Failed to reload desktop:", error);
          } finally {
            refreshDirtyStates();
          }
        },
      });
    },
    [refreshDirtyStates],
  );

  const openAllDesktops = useCallback(() => {
    eventBus.emit(EVENTS.OPEN_LOAD_DESKTOP_DIALOG);
  }, []);

  return {
    sections,
    hasFavorites,
    favoriteIds: favoriteDesktops,
    toggleFavorite: toggleFavoriteDesktop,
    currentDesktop,
    /**
     * false até o primeiro fetch resolver. Superfícies que PUBLICAM estado (o
     * menu nativo) devem esperar: a árvore do Main remonta (key themeReady) e,
     * sem essa guarda, o menu piscaria "Nenhum favorito" a cada remontagem.
     */
    loaded,
    currentDesktopId,
    isDirty,
    switchTo,
    confirmReload,
    openAllDesktops,
    refreshDirtyStates,
  };
};
